import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import type { IWindowService } from "angular";
import type { MomentInput } from 'moment';
import moment from 'moment';
import Calendar from './baremetrics-calendar.js';
import { CVLocalizationService } from '../services/cv-localization.service.js';

/* eslint-disable @typescript-eslint/naming-convention */

@Component({
    selector: 'cv-baremetrics-calendar',
    templateUrl: './baremetrics-calendar.component.html',
    styleUrls: ['./baremetrics-calendar.component.scss']
})
export class BaremetricsCalendarComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() earliestDate: MomentInput;
    @Input() latestDate: MomentInput;
    @Input() inputFormat: string;
    @Input() jumpMonthFormat: string;
    @Input() jumpYearFormat: string;
    @Input() presetFormat: string;
    @Input() daysArray: string[];

    @Input() required = true;
    @Input() placeholder: string;
    @Input() allowSameDay: boolean;
    @Input() presets: BareMetricsCalendarPreset[];
    @Input() mode: 'single' | 'double';
    @Input() dropdownOnly: boolean = false;
    @Input() disabled: boolean = false;

    // outputs required for two-way binding, e.g. [(startDate)]="blah" means [startDate]="blah" (startDateChange)="blah = $event"
    @Output() currentDateChange = new EventEmitter<Date>();
    @Output() startDateChange = new EventEmitter<Date>();
    @Output() endDateChange = new EventEmitter<Date>();
    @Output() selectedPresetChange = new EventEmitter<string>();

    // output for a full date change event
    @Output() dateChanged = new EventEmitter<Calendar>();


    _currentDate: Date;
    get currentDate() {
        return this._currentDate;
    }

    @Input()
    set currentDate(val: Date) { 
        //First part of the date is always a string
        if (typeof (val) === 'string') {
            //val comes with Date properties and we need to use replace function from a string variable
            let stringVal: string = val;
            val = new Date(stringVal.replace(/-/g, '\/'));
        }
        //Second part of the date is always a date object to get the time
        if (typeof (val) === 'object') {
            if (this.initialized && val == null && this.required) {
                val = new Date();
                this.currentDateChange.emit(val);
            }
        }

        if (this.calendar != null && !moment(this.calendar.current_date).isSame(moment(val))) {
            this.calendar.current_date = val == null ? null : moment(val);
            this.calendar.calendarExternalUpdateHtml();
        }

        if (val === this._currentDate || val?.getTime() === this._currentDate?.getTime())
            return;

        this._currentDate = val;
        this.currentDateChange.emit(val);
    }

    _startDate: Date;
    get startDate() {
        return this._startDate;
    }

    @Input()
    set startDate(val: Date) {
        if (val === this._startDate || val?.getTime() === this._startDate?.getTime())
            return;

        if (this.calendar != null && val != null) {
            this.calendar.start_date = moment(val);
            this.calendar.calendarSaveDates();
        }

        this._startDate = val;
        this.startDateChange.emit(val);
    }

    _endDate: Date;
    get endDate() {
        return this._endDate;
    }

    @Input()
    set endDate(val: Date) {
        if (val === this._endDate || val?.getTime() === this._endDate?.getTime())
            return;

        if (this.calendar != null && val != null) {
            this.calendar.end_date = moment(val);
            this.calendar.calendarSaveDates();
        }

        this._endDate = val;
        this.endDateChange.emit(val);
    }

    _selectedPreset: string;
    get selectedPreset() {
        return this._selectedPreset;
    }

    @Input()
    set selectedPreset(val: string) {
        if (this.calendar != null && val != null) {
            this.calendar.presetSelect(val);
        }

        if (val === this._selectedPreset)
            return;

        this._selectedPreset = val;
        this.selectedPresetChange.emit(val);
    }

    private calendar: BaremetricsCalendar;
    private initialized = false;
    private localization: Map<string, string>;

    constructor(
        private $element: ElementRef<HTMLElement>,
        @Inject('$window') private $window: IWindowService,
        private ngZone: NgZone,
        private cvLocalizationService: CVLocalizationService,
    ) {

    }

    initialize() {
        this.initialized = true;

        if (this.required && this.currentDate == null)
            this.currentDate = new Date();

        const settings: BaremetricsCalendarSettings = {
            element: $(this.$element.nativeElement),
            earliest_date: this.earliestDate,
            latest_date: this.latestDate,
            format: {
                input: this.inputFormat ?? this.$window['CVMomentDateFormat'],
                jump_month: this.jumpMonthFormat,
                jump_year: this.jumpYearFormat,
                preset: this.presetFormat ?? this.$window['CVMomentDateFormat'],
            },
            days_array: this.daysArray,
            disabled: this.disabled,
            dropdownOnly: this.dropdownOnly,

            // only used on single dates
            current_date: this.currentDate,
            required: this.required,
            placeholder: this.placeholder,

            // only used on date ranges
            start_date: this.startDate,
            end_date: this.endDate,
            same_day_range: this.allowSameDay ?? true,
            presets: this.presets ?? false,
            localization: this.localization,

            callback: () => {
                this.calendar.calendarSetDates();
                const currentDate: Date = this.calendar.current_date?.toDate() ?? null;
                const startDate: Date = this.calendar.start_date?.toDate();
                const endDate: Date = this.calendar.end_date?.toDate();
                const selectedPreset = this.calendar.selected_preset;

                this.ngZone.run(() => {
                    if ((!this.required || currentDate != null) && currentDate?.getTime() !== this._currentDate?.getTime()) {
                        this._currentDate = currentDate;
                        this.currentDateChange.emit(this._currentDate);
                    }

                    if (startDate != null && startDate.getTime() !== this._startDate?.getTime()) {
                        this._startDate = startDate;
                        this.startDateChange.emit(this._startDate);
                    }

                    if (endDate != null && endDate.getTime() !== this._endDate?.getTime()) {
                        this._endDate = endDate;
                        this.endDateChange.emit(this._endDate);
                    }

                    if (selectedPreset !== this._selectedPreset) {
                        this._selectedPreset = selectedPreset;
                        this.selectedPresetChange.emit(this._selectedPreset);
                    }

                    this.dateChanged.emit(this.calendar);
                });
            },
        };

        setTimeout(() => {
            this.calendar = new Calendar(settings);
            if (this.selectedPreset != null) {
                this.calendar.presetSelect(this.selectedPreset);
            }

            if(this.localization) {
                this.calendar.createScrollbar();
            }
        });
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes.mode?.isFirstChange())
        {
            if (changes.mode.currentValue === "double")
            {
                this.$element.nativeElement.classList.add("d-block");
                this.$element.nativeElement.classList.add("daterange");
                this.$element.nativeElement.classList.add("daterange--double");
                this.$element.nativeElement.classList.remove("daterange--single");
            }
            else if (changes.mode.currentValue === "single")
            {
                this.$element.nativeElement.classList.add("d-block");
                this.$element.nativeElement.classList.add("daterange");
                this.$element.nativeElement.classList.add("daterange--single");
                this.$element.nativeElement.classList.remove("daterange--double");
            }
        }

        if (changes.earliestDate && this.calendar) {
            this.calendar.setEarliestDate(this.earliestDate);
        }
        
        if (changes.latestDate && this.calendar) {
            this.calendar.setLatestDate(this.latestDate);
        }

        if (!this.initialized && (
            changes.currentDate?.currentValue !== undefined ||
            changes.startDate?.currentValue !== undefined ||
            changes.selectedPreset?.currentValue !== undefined
        )) {
            this.localization = await this.cvLocalizationService.GetSection("Common");
            this.initialize();
        }
    }

    ngOnInit() {

    }

    ngAfterViewInit() {

    }
}

export interface BareMetricsCalendarFormatSettings {
    /** Format for the input fields e.g. 'MMMM D, YYYY' */
    input?: string;

    /** Format for the month switcher e.g. 'MMMM' */
    jump_month?: string;

    /** Format for the year switcher e.g. 'YYYY' */
    jump_year?: string;

    /** Format for the preset field dates e.g. 'MMMM D, YYYY' */
    preset?: string;
}
export interface BaremetricsCalendar {
    settings: BaremetricsCalendarSettings;
    calIsOpen: boolean;
    presetIsOpen: boolean;
    sameDayRange: boolean;
    element: JQuery;
    selected: JQuery;
    type: string;
    required: boolean;
    format: BareMetricsCalendarFormatSettings;
    placeholder: string;
    days_array: string[];
    orig_start_date: moment.Moment;
    orig_end_date: moment.Moment;
    orig_current_date: moment.Moment;
    earliest_date: moment.Moment;
    latest_date: moment.Moment;
    end_date: moment.Moment;
    start_date: moment.Moment;
    current_date: moment.Moment;
    selected_preset: string;
    presets: boolean;
    callback: () => void;
    new(settings: BaremetricsCalendarSettings);
    presetSelect(preset_name: string): void;
    presetToggle(): void;
    presetCreate(): JQuery<HTMLElement>;
    calendarSetDates(): void;
    calendarExternalUpdateHtml(): void;
    calendarSaveDates(): any;
    calendarCheckDate(d: string): moment.Moment;
    calendarCheckDates(): void;
    stringToDate(str: string): moment.Moment;
    calendarOpen(selected: JQuery, switcher: JQuery): void;
    calendarClose(type: string): boolean;
    calendarCreate(switcher: JQuery): void;
    calendarArray(start: any, end: any, current: any, switcher: any): {
        str: number;
        start: boolean;
        end: boolean;
        current: boolean;
        selected: boolean;
        date: string;
        outside: boolean;
        fade: boolean;
    }[];
    calendarHTML(type: any): HTMLElement;
    parseDate(d: string): moment.Moment;
    range(length: any): any[];
    selectOneDate(other: any, cal: any, date: any): void;
    setEarliestDate(earliestDate: MomentInput): void;
    setLatestDate(latestDate: MomentInput): void;
    createScrollbar(): void;
}


export interface BareMetricsCalendarPreset {
    name: string;
    label: string;
    start: MomentInput;
    end: MomentInput;
}

export interface BaremetricsCalendarSettings {
    // Base Calendar Params

    /** jQuery DOM object of the calendar div you're working on */
    element: JQuery;

    /** The earliest date to show in the calendar */
    earliest_date?: MomentInput;

    /** The latest date to show in the calendar */
    latest_date?: MomentInput;

    /** Dropdown only mode for date ranges */
    dropdownOnly?: boolean;

    /** A function for whenever a new date is saved
     * Inside you have access to object variables like this.earliest_date and this.latest_date for doing things with your calendar's dates
     */
    callback?: () => void;

    /** Object containing formatting strings for.. you guessed it.. formatting your dates */
    format?: BareMetricsCalendarFormatSettings;

    /** Array of the 7 strings you'd like to represent your days in the calendar e.g. ['S','M','T','W','T','F','S'] */
    days_array?: string[];

    // Single Calendar Params

    /** The date to start the calendar on */
    current_date?: MomentInput;

    /** Toggle if this field must have always have a valid selected date */
    required?: boolean;

    /** Set placeholder text (note this will only apply if the required key is set to false). The default will be whatever moment date format you're using. (i.e. 'M/D/YYYY') */
    placeholder?: string;

    // Double Calendar Params

    /** The date to start the selection on for the calendar */
    start_date?: MomentInput;

    /** The date to end the selection on for the calendar */
    end_date?: MomentInput;

    /** Allow a range selection of a single day */
    same_day_range?: boolean;

    /** If you don't want to show the preset link just set this to false otherwise the default is true which will just give you a basic preset of.. yep.. presets. BOOM!
     * Otherwise, if you want to customize it up you can include an array of preset objects.
     */
    presets?: BareMetricsCalendarPreset[] | boolean;

    /** The localization map */
    localization?: Map<string, string>;

    /** Whether the calendar is currently disabled */
    disabled?: boolean;
}
